/**
 * The two robots' own reading and write — one fold for the vacuum's and the
 * mower's sheets, their tiles and the verbs they drive (card t_390bc438, V6 of
 * the Matter virtual-device programme).
 *
 * The hub models both classes on Matter's **Robotic Vacuum Cleaner** (RVC)
 * cluster set (`otto/src/device/classes/unified/extras.ts`, `classId: 'vacuum'`
 * and `classId: 'lawn_mower'`), and the values arrive carrying that vocabulary:
 *
 *   · `command`    Control, settable — the verbs `Start · Pause · Stop · Resume
 *                  · Dock` (`ROBOT_COMMANDS`). The hub writes them to
 *                  RvcOperationalState 0x0061/0x0004 as the matching cluster
 *                  command.
 *   · `mode`       Control, settable — the run mode (`Idle` / `Cleaning` or
 *                  `Mowing` / `Mapping`, RvcRunMode 0x0054/0x0001).
 *   · `clean_mode` Control, settable — the operator's own pick: the vacuum's
 *                  suction (`Quiet`/`Standard`/`Turbo`/`Max`) and the mower's
 *                  cut height (`Low`/`Medium`/`High`) (RvcCleanMode
 *                  0x0055/0x0001). The whole point of the selector.
 *   · `state`      Status, **read-only** — the operational state, one of the
 *                  seven `ROBOT_STATES` below.
 *   · `battery`    Status, read-only, 0–100 %.
 *   · `progress`   Status, read-only, 0–100 % — the job the machine is on.
 *                  (Matter has no progress attribute; it is the hub's own.)
 *   · `error`      Status, read-only — a free string, empty when there is none.
 *   · `last_run` / `next_run`  the mower only: the schedule stamps the demo
 *                  animation writes (`"Today 13:59"`).
 *
 * The keys are the same on both classes; the two differ in the vocabulary of
 * `mode` / `clean_mode` and in that schedule pair, so the kind is a parameter of
 * the folds instead of a second module. `power` (the started/stopped boolean the
 * class keeps for its Home Assistant link) is deliberately NOT read here: the
 * RVC verbs are the control, and a second write path to the same machine would
 * be a second truth.
 *
 * The values the class declares are **not** stored on the device rows (the
 * enum choices are served per value by the options endpoint), so every fold
 * takes the row list *and* the widget's live socket bag, and answers `null` /
 * `''` for a reading it does not have rather than a guess.
 */

/** One device-value row as the robots read it. */
export type RobotValueRow = {
  valueKey?: unknown;
  property_key?: unknown;
  valueType?: unknown;
  /** The hub's own settable flag — the sheet's read-only gate. */
  modeSettable?: unknown;
  currentValue?: unknown;
  enum_options?: { value?: unknown; label?: unknown }[];
};

/** Which of the two robots a row is. */
export type RobotKind = 'vacuum' | 'mower';

export const ROBOT_COMMAND_KEY = 'command';
export const ROBOT_MODE_KEY = 'mode';
export const ROBOT_CLEAN_MODE_KEY = 'clean_mode';
export const ROBOT_STATE_KEY = 'state';
export const ROBOT_BATTERY_KEY = 'battery';
export const ROBOT_PROGRESS_KEY = 'progress';
export const ROBOT_ERROR_KEY = 'error';
export const ROBOT_STATUS_KEY = 'status';
export const ROBOT_LAST_RUN_KEY = 'last_run';
export const ROBOT_NEXT_RUN_KEY = 'next_run';

/**
 * The operational states, verbatim from the hub's `RVC_OPERATIONAL_STATES`
 * (Matter 1.2 `OperationalStateEnum`: Stopped 0, Running 1, Paused 2, Error 3,
 * SeekingCharger 64, Charging 65, Docked 66). The class keeps the *name* as the
 * value, so these are the words a device reports.
 */
export const ROBOT_STATES = [
  'Stopped',
  'Running',
  'Paused',
  'Error',
  'SeekingCharger',
  'Charging',
  'Docked',
] as const;

export type RobotState = (typeof ROBOT_STATES)[number];

/** The RVC verbs, in the hub's own order (`ROBOT_COMMANDS`). */
export const ROBOT_COMMANDS = ['Start', 'Pause', 'Stop', 'Resume', 'Dock'] as const;

export type RobotCommand = (typeof ROBOT_COMMANDS)[number];

/** The run modes, per kind (`VACUUM_RUN_MODES` / `MOWER_RUN_MODES`). */
export const VACUUM_RUN_MODES = ['Idle', 'Cleaning', 'Mapping'] as const;
export const MOWER_RUN_MODES = ['Idle', 'Mowing', 'Mapping'] as const;

/** The vacuum's suction settings, the hub's own four (`VACUUM_CLEAN_MODES`). */
export const VACUUM_CLEAN_MODES = ['Quiet', 'Standard', 'Turbo', 'Max'] as const;

/** The mower's cut heights (`MOWER_CUT_HEIGHTS`). */
export const MOWER_CUT_HEIGHTS = ['Low', 'Medium', 'High'] as const;

/** The two kinds' own lexicons, both languages (the app's `tt()` pair shape). */
export const ROBOT_WORDS: Record<RobotKind, {
  /** The sheet's hero caption while the machine is home. */
  docked: { en: string; sl: string };
  /** The `clean_mode` row's title — the operator's own pick on this machine. */
  cleanMode: { en: string; sl: string };
  runModes: readonly string[];
  cleanModes: readonly string[];
  /** The reading tiles this kind carries beyond the two both share. */
  schedule: boolean;
}> = {
  vacuum: {
    docked: { en: 'On the dock', sl: 'Na postaji' },
    cleanMode: { en: 'Suction', sl: 'Moč sesanja' },
    runModes: VACUUM_RUN_MODES,
    cleanModes: VACUUM_CLEAN_MODES,
    schedule: false,
  },
  mower: {
    docked: { en: 'On the dock', sl: 'Na postaji' },
    cleanMode: { en: 'Cutting height', sl: 'Višina košnje' },
    runModes: MOWER_RUN_MODES,
    cleanModes: MOWER_CUT_HEIGHTS,
    schedule: true,
  },
};

/** The operational state words, in both languages. */
export const ROBOT_STATE_LABELS: Record<RobotState, { en: string; sl: string }> = {
  Stopped: { en: 'Stopped', sl: 'Ustavljen' },
  Running: { en: 'Running', sl: 'V teku' },
  Paused: { en: 'Paused', sl: 'V premoru' },
  Error: { en: 'Error', sl: 'Napaka' },
  // The one camelCase state, spaced for the chip — the same name the hub and
  // the log carry, in the spelling a chip can wear.
  SeekingCharger: { en: 'Seeking charger', sl: 'Išče polnilnico' },
  Charging: { en: 'Charging', sl: 'Polnjenje' },
  Docked: { en: 'Docked', sl: 'Na postaji' },
};

/** The verbs' own words, in both languages. */
export const ROBOT_COMMAND_LABELS: Record<RobotCommand, { en: string; sl: string }> = {
  Start: { en: 'Start', sl: 'Začni' },
  Pause: { en: 'Pause', sl: 'Premor' },
  Stop: { en: 'Stop', sl: 'Ustavi' },
  Resume: { en: 'Resume', sl: 'Nadaljuj' },
  Dock: { en: 'Dock', sl: 'Na postajo' },
};

/** The run modes' words, both kinds (they share `Idle` / `Mapping`). */
export const ROBOT_RUN_MODE_LABELS: Record<string, { en: string; sl: string }> = {
  Idle: { en: 'Idle', sl: 'Miruje' },
  Cleaning: { en: 'Cleaning', sl: 'Čiščenje' },
  Mowing: { en: 'Mowing', sl: 'Košnja' },
  Mapping: { en: 'Mapping', sl: 'Kartiranje' },
};

/** The clean-mode words — the vacuum's suction levels and the mower's heights. */
export const ROBOT_CLEAN_MODE_LABELS: Record<string, { en: string; sl: string }> = {
  Quiet: { en: 'Quiet', sl: 'Tiho' },
  Standard: { en: 'Standard', sl: 'Standard' },
  Turbo: { en: 'Turbo', sl: 'Turbo' },
  Max: { en: 'Max', sl: 'Največ' },
  Low: { en: 'Low', sl: 'Nizko' },
  Medium: { en: 'Medium', sl: 'Srednje' },
  High: { en: 'High', sl: 'Visoko' },
};

/**
 * Which robot a device row is, or `null` for everything else.
 *
 * Both classes carry `controlType: "switch"` (their metadata), so the control
 * fold alone lands them on the generic switch card; their `deviceType` is the
 * field that names the machine. A commissioned Matter RVC arrives as
 * `robotic_vacuum_cleaner` (device type 116 → the `vacuum` class), which the
 * substring test catches, and the mower's Torik device type is `lawn_mower`.
 *
 * The class icon (`vacuum` / `grass`) is the second net, for a row whose type a
 * user retyped in the device editor — and it is deliberately a narrow one: it
 * only reaches devices that would otherwise render as the generic switch card,
 * because the icon is a user's own pick and a lamp painted with the grass icon
 * is still a lamp. The device NAME is not read at all: "Vacuum Socket" is a
 * plug, and the switch card's own fold already owns those.
 */
export function robotKindOf(device: {
  deviceType?: string | null;
  controlType?: string | null;
  deviceControl?: string | null;
  icon?: string | null;
}): RobotKind | null {
  const type = [device.deviceType, device.controlType, device.deviceControl]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (type.includes('vacuum')) return 'vacuum';
  if (type.includes('mower')) return 'mower';

  const icon = (device.icon ?? '').trim().toLowerCase();
  const byIcon: RobotKind | null =
    icon === 'vacuum' || icon === 'robot_vacuum'
      ? 'vacuum'
      : icon === 'grass' || icon === 'mower' || icon === 'lawn_mower'
        ? 'mower'
        : null;
  if (byIcon === null) return null;

  const control = [device.controlType, device.deviceControl].filter(Boolean).join(' ').toLowerCase();
  if (control !== '' && !control.includes('switch')) return null;
  return byIcon;
}

/** The row's own key, whichever spelling it arrived under. */
function rowKey(row: RobotValueRow): string {
  const key = typeof row.valueKey === 'string' && row.valueKey !== '' ? row.valueKey : row.property_key;
  return typeof key === 'string' ? key : '';
}

/** The rows, either spelling the device row arrived under. */
function rowsOf(rows: readonly RobotValueRow[] | null | undefined): readonly RobotValueRow[] {
  return Array.isArray(rows) ? rows : [];
}

/**
 * A reading: the live socket bag first (the widget's subscription), else the
 * device row's own value — the order `lockState`, `garageDoorState` and the
 * sheet chrome all use.
 */
export function robotReading(
  rows: readonly RobotValueRow[] | null | undefined,
  live: Record<string, unknown>,
  key: string,
): unknown {
  if (key in live) return live[key];
  return rowsOf(rows).find((row) => rowKey(row) === key)?.currentValue;
}

/** Whether the device carries a value key at all (the sheet's has-a-row gate). */
export function robotHasRow(rows: readonly RobotValueRow[] | null | undefined, key: string): boolean {
  return rowsOf(rows).some((row) => rowKey(row) === key);
}

/**
 * Whether a row may be written through, per the hub's own `modeSettable`.
 *
 * The class declares `command` and `clean_mode` settable and `state`,
 * `battery`, `progress`, `error` read-only, and the hub's row is the authority
 * (`modeSettable !== false` — the read a device-value picker already uses, so a
 * row from a payload that does not carry the flag is not silently locked).
 */
export function robotWritable(rows: readonly RobotValueRow[] | null | undefined, key: string): boolean {
  const row = rowsOf(rows).find((candidate) => rowKey(candidate) === key);
  return row !== undefined && row.modeSettable !== false;
}

/** The device's own enum choices for a row, in the order the hub serves them. */
export function robotEnumOptions(
  rows: readonly RobotValueRow[] | null | undefined,
  key: string,
): { value: string; label: string }[] {
  const raw = rowsOf(rows).find((row) => rowKey(row) === key)?.enum_options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((option) => ({ value: String(option?.value ?? ''), label: String(option?.label ?? '') }))
    .filter((option) => option.value !== '');
}

/**
 * The operational state the device reports, or `null`.
 *
 * Only a word from the hub's own seven is a reading: a hub that grows a state
 * this build has not learned (or a device that reports a number) answers
 * `null`, which every surface below renders as "no state" rather than as an
 * invented word.
 */
export function robotState(
  rows: readonly RobotValueRow[] | null | undefined,
  live: Record<string, unknown> = {},
): RobotState | null {
  const raw = robotReading(rows, live, ROBOT_STATE_KEY);
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  return (ROBOT_STATES as readonly string[]).includes(text) ? (text as RobotState) : null;
}

/** The run-mode word the device reports (its own vocabulary), or `''`. */
export function robotRunMode(
  rows: readonly RobotValueRow[] | null | undefined,
  live: Record<string, unknown> = {},
): string {
  const raw = robotReading(rows, live, ROBOT_MODE_KEY);
  return typeof raw === 'string' ? raw.trim() : '';
}

/** The clean-mode word the device reports (its own vocabulary), or `''`. */
export function robotCleanMode(
  rows: readonly RobotValueRow[] | null | undefined,
  live: Record<string, unknown> = {},
): string {
  const raw = robotReading(rows, live, ROBOT_CLEAN_MODE_KEY);
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * A percentage reading (battery, progress), clamped to 0–100 — or `null` when
 * the row carries nothing. A stray raw byte cannot read as 255 %.
 */
export function robotPercent(
  rows: readonly RobotValueRow[] | null | undefined,
  live: Record<string, unknown>,
  key: string,
): number | null {
  const raw = robotReading(rows, live, key);
  if (raw === undefined || raw === null || raw === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** A text reading (`error`, `last_run`, `next_run`, the legacy `status`), trimmed; `''` when empty. */
export function robotText(
  rows: readonly RobotValueRow[] | null | undefined,
  live: Record<string, unknown>,
  key: string,
): string {
  const raw = robotReading(rows, live, key);
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Whether the machine is out doing its job right now (the drawings turn). */
export function robotRunning(state: RobotState | null): boolean {
  return state === 'Running';
}

/**
 * Whether the machine is away from its dock — the tile's active reading. A
 * paused robot is still out on the job (it holds its place), and a returning
 * one is on its way home; only the dock and the charger are rest.
 */
export function robotAway(state: RobotState | null): boolean {
  return state === 'Running' || state === 'Paused' || state === 'SeekingCharger';
}

/** Whether the machine is resting on its dock / charger. */
export function robotDocked(state: RobotState | null): boolean {
  return state === 'Docked' || state === 'Charging';
}

/**
 * The verbs that apply in a state — the sheet shows the four always
 * (`Start · Pause · Stop · Dock`, with the inapplicable ones disabled) and
 * `Resume` only where it applies, which is what `robotHasResume` decides.
 *
 * The table is the RVC cluster's own semantics read as an operator would:
 *
 *   · a running robot takes Pause, Stop or a Dock;
 *   · a paused one takes Resume (or Stop / Dock);
 *   · a stopped or home robot takes Start (a docked machine starts its job);
 *   · a returning one takes Stop or Dock (it is already on its way);
 *   · an errored one takes Start (retry), Stop or Dock.
 *
 * An **unknown** state (a hub that has not reported one yet) answers the full
 * four: the sheet must stay usable on a device whose state is not known, and it
 * is the hub's own job to refuse a verb that makes no sense.
 */
export function robotVerbsFor(state: RobotState | null): RobotCommand[] {
  switch (state) {
    case 'Running':
      return ['Pause', 'Stop', 'Dock'];
    case 'Paused':
      return ['Resume', 'Stop', 'Dock'];
    case 'Stopped':
      return ['Start', 'Dock'];
    case 'Docked':
    case 'Charging':
      return ['Start'];
    case 'SeekingCharger':
      return ['Stop', 'Dock'];
    case 'Error':
      return ['Start', 'Stop', 'Dock'];
    default:
      return ['Start', 'Pause', 'Stop', 'Dock'];
  }
}

/** Whether Resume is one of the state's verbs — the one the sheet adds on demand. */
export function robotHasResume(state: RobotState | null): boolean {
  return robotVerbsFor(state).includes('Resume');
}

/** Whether a verb applies in a state (the chip's own enabled reading). */
export function robotVerbApplies(state: RobotState | null, verb: RobotCommand): boolean {
  return robotVerbsFor(state).includes(verb);
}

/** The state's word in both languages, or `''` for an unknown state. */
export function robotStateWord(state: RobotState | null): { en: string; sl: string } | null {
  if (state === null) return null;
  return ROBOT_STATE_LABELS[state];
}
